/**
 * ListsManager component for managing notification lists
 * Table design matching NotificationAuditLog style
 * Now with detail view for viewing/editing lists
 */

import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Plus, Users, Mail, Phone, Filter, Eye, X, AlertCircle, Calendar, Trash2, CheckCircle, Clock, Snowflake, XCircle, Check } from 'lucide-react';
import {
  NotificationList,
  NotificationChannel,
  FilterCriteria,
  DateFilterType,
  ListCreateRequest,
  ListPreview,
  getLists,
  createList,
  updateList,
  deleteList,
  previewList,
} from '../../utils/notificationsBroadcastApi';
import { ListDetailView } from './ListDetailView';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';

interface ListsManagerProps {
  onSelectList?: (list: NotificationList) => void;
  onListNameChange?: (name: string | null) => void;
}

export interface ListsManagerHandle {
  openCreate: () => void;
  openEdit: () => void;
  getSelectedListName: () => string | null;
}

const SPECIES_OPTIONS = [
  { value: 'dog', label: 'Dogs' },
  { value: 'cat', label: 'Cats' },
  { value: 'bird', label: 'Birds' },
  { value: 'reptile', label: 'Reptiles' },
  { value: 'small mammal', label: 'Small Mammals' },
  { value: 'exotic', label: 'Exotic' },
];

const STATUS_OPTIONS = [
  { value: 'PROSPECT', label: 'Prospect' },
  { value: 'OPEN', label: 'Active' },
  { value: 'COLD', label: 'Needs Appt' },
  { value: 'LOST', label: 'Lost' },
];

// Status icon and styling - matching the "Delivered" badge style
const getStatusConfig = (status: string): { icon: React.ReactNode; className: string } => {
  switch (status.toUpperCase()) {
    case 'OPEN':
      return {
        icon: <CheckCircle className="w-3 h-3" />,
        className: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
      };
    case 'PROSPECT':
      return {
        icon: <Clock className="w-3 h-3" />,
        className: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
      };
    case 'COLD':
      return {
        icon: <Snowflake className="w-3 h-3" />,
        className: 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400'
      };
    case 'LOST':
      return {
        icon: <XCircle className="w-3 h-3" />,
        className: 'bg-gray-100 dark:bg-gray-700/30 text-gray-600 dark:text-gray-400'
      };
    default:
      return {
        icon: null,
        className: 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400'
      };
  }
};

export const ListsManager = forwardRef<ListsManagerHandle, ListsManagerProps>(
  ({ onSelectList, onListNameChange }, ref) => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const selectedListId = searchParams.get('listId');
    
  const [lists, setLists] = useState<NotificationList[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [listToDelete, setListToDelete] = useState<NotificationList | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

    // Form state for create modal
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formListType, setFormListType] = useState<'email' | 'phone'>('email');
  const [formSpecies, setFormSpecies] = useState<string[]>([]);
  const [formCustomerStatus, setFormCustomerStatus] = useState<string[]>([]);
  const [formDateFilterType, setFormDateFilterType] = useState<DateFilterType | ''>('');
  const [formDateAfter, setFormDateAfter] = useState('');
  const [formDateBefore, setFormDateBefore] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);

  const loadLists = async () => {
    try {
      setLoading(true);
      const data = await getLists();
      setLists(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load lists');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLists();
  }, []);

  // Update parent with selected list name when it changes
  useEffect(() => {
    if (selectedListId && lists.length > 0) {
      const list = lists.find(l => l.id === selectedListId);
      onListNameChange?.(list?.name || null);
    } else {
      onListNameChange?.(null);
    }
  }, [selectedListId, lists, onListNameChange]);

  // Check for create=true query param to auto-open create modal
  useEffect(() => {
    const shouldCreate = searchParams.get('create') === 'true';
    if (shouldCreate) {
      openCreateModal();
      // Remove the create param from URL
      navigate('/dashboard/audience', { replace: true });
    }
  }, [searchParams, navigate]);

    // Handle escape key for modal close
    const handleKeyDown = useCallback((e: KeyboardEvent) => {
      if (e.key === 'Escape' && showCreateModal) {
        setShowCreateModal(false);
      }
    }, [showCreateModal]);

    useEffect(() => {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

  const resetForm = () => {
    setFormName('');
    setFormDescription('');
    setFormListType('email');
    setFormSpecies([]);
    setFormCustomerStatus([]);
    setFormDateFilterType('');
    setFormDateAfter('');
    setFormDateBefore('');
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

    // Expose openCreate, openEdit, and getSelectedListName to parent via ref
    useImperativeHandle(ref, () => ({
      openCreate: openCreateModal,
      openEdit: () => {
        // Trigger edit mode by adding edit param to URL
        if (selectedListId) {
          navigate(`/dashboard/audience?listId=${selectedListId}&edit=true`);
        }
      },
      getSelectedListName: () => {
        if (selectedListId) {
          const list = lists.find(l => l.id === selectedListId);
          return list?.name || null;
        }
        return null;
      }
    }), [selectedListId, navigate, lists]);

    const handleSelectList = (list: NotificationList) => {
      // Use navigate for proper browser history
      navigate(`/dashboard/audience?listId=${list.id}`);
    };

    const handleBackToList = () => {
      // Use navigate for proper browser history
      navigate('/dashboard/audience');
      loadLists(); // Refresh the list
  };

    const handleSubmitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitting(true);

    const filterCriteria: FilterCriteria = {
      species: formSpecies.length > 0 ? formSpecies : undefined,
      customer_status: formCustomerStatus.length > 0 ? formCustomerStatus : undefined,
    };

    // Add date filter if specified
    if (formDateFilterType) {
      filterCriteria.customer_created_date_type = formDateFilterType;
      if (formDateFilterType === 'after' && formDateAfter) {
        filterCriteria.customer_created_after = formDateAfter;
      } else if (formDateFilterType === 'before' && formDateBefore) {
        filterCriteria.customer_created_before = formDateBefore;
      } else if (formDateFilterType === 'between' && formDateAfter && formDateBefore) {
        filterCriteria.customer_created_after = formDateAfter;
        filterCriteria.customer_created_before = formDateBefore;
      }
    }

    const data: ListCreateRequest = {
      name: formName,
      description: formDescription || undefined,
      list_type: formListType,
      filter_criteria: filterCriteria,
    };

    try {
        const newList = await createList(data);
      setShowCreateModal(false);
      resetForm();
      await loadLists();
        // Navigate to the new list detail view - use navigate for proper browser history
        navigate(`/dashboard/audience?listId=${newList.id}`);
    } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create list');
    } finally {
      setFormSubmitting(false);
    }
  };

    const handleDeleteClick = (e: React.MouseEvent, list: NotificationList) => {
      e.stopPropagation(); // Prevent row click
      setListToDelete(list);
      setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
      if (!listToDelete) return;
      
      setIsDeleting(true);
      try {
        await deleteList(listToDelete.id);
        setDeleteModalOpen(false);
        setListToDelete(null);
        await loadLists();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete list');
      } finally {
        setIsDeleting(false);
      }
    };

  const toggleSpecies = (species: string) => {
    setFormSpecies((prev) =>
      prev.includes(species) ? prev.filter((s) => s !== species) : [...prev, species]
    );
  };

  const toggleStatus = (status: string) => {
    setFormCustomerStatus((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    };

    // If a list is selected, show the detail view
    if (selectedListId) {
      const shouldEdit = searchParams.get('edit') === 'true';
      return <ListDetailView listId={selectedListId} onBack={handleBackToList} startEditing={shouldEdit} />;
  }

  return (
      <div className="h-full flex flex-col">
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="w-4 h-4" />
        </button>
      </div>
        )}

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : lists.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-dark-text-muted">
              <Users className="w-12 h-12 mb-3 opacity-50" />
              <p className="text-lg font-medium">No audiences yet</p>
              <p className="text-sm mb-4">Create your first audience to start sending targeted notifications</p>
          <button
            onClick={openCreateModal}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            Audience
          </button>
        </div>
      ) : (
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-dark-bg-tertiary sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                    Audience
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                    Pets
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                    Client Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                    Delete
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-dark-bg-secondary divide-y divide-gray-200 dark:divide-dark-border">
          {lists.map((list) => (
                  <tr
              key={list.id}
                    onClick={() => handleSelectList(list)}
                    className="hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors cursor-pointer"
            >
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                        <Check className="w-3 h-3" />
                        Active
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                          {list.name}
                        </div>
                        {list.description && (
                          <div className="text-xs text-gray-500 dark:text-dark-text-muted truncate max-w-xs">
                            {list.description}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {list.filter_criteria.species?.length ? (
                          list.filter_criteria.species.map((s) => (
                            <span
                              key={s}
                              className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 rounded"
                            >
                              {s}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-dark-text-muted">All pets</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {list.filter_criteria.customer_status?.length ? (
                          list.filter_criteria.customer_status.map((s) => {
                            const config = getStatusConfig(s);
                            const label = STATUS_OPTIONS.find(opt => opt.value === s)?.label || s;
                            return (
                              <span
                                key={s}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded ${config.className}`}
                              >
                                {config.icon}
                                {label}
                              </span>
                            );
                          })
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-dark-text-muted">All statuses</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-dark-text-secondary">
                        <Calendar className="w-3 h-3" />
                        {formatDate(list.created_at)}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <button
                        onClick={(e) => handleDeleteClick(e, list)}
                        className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
                )}
              </div>

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-dark-bg-secondary rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-border">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
                      Create Audience
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-dark-text-muted">Define your audience</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
            </div>

              {/* Content */}
              <form onSubmit={handleSubmitCreate} className="p-6 space-y-6">
              {/* Name */}
              <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                  List Name
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg dark:bg-dark-bg-tertiary dark:text-dark-text-primary text-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Cat Owners, Active Customers"
                />
              </div>

              {/* Description */}
              <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                  Description (optional)
                </label>
                <input
                  type="text"
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg dark:bg-dark-bg-tertiary dark:text-dark-text-primary text-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="What this list is for..."
                />
              </div>

                {/* Filters Section */}
                <div className="border border-gray-200 dark:border-dark-border rounded-lg p-4 bg-gray-50 dark:bg-dark-bg-tertiary space-y-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-dark-text-secondary">
                  <Filter className="w-4 h-4" />
                  Filters
                </div>

                {/* Species Filter */}
                <div>
                    <label className="block text-sm text-gray-600 dark:text-dark-text-muted mb-2">
                      Pet Species <span className="text-xs">(leave empty for all)</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {SPECIES_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => toggleSpecies(option.value)}
                          className={`px-3 py-1.5 text-sm font-medium border rounded-lg transition-colors ${
                          formSpecies.includes(option.value)
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                              : 'border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-secondary text-gray-700 dark:text-dark-text-primary hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Customer Status Filter */}
                <div>
                    <label className="block text-sm text-gray-600 dark:text-dark-text-muted mb-2">
                      Customer Status <span className="text-xs">(leave empty for all)</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {STATUS_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => toggleStatus(option.value)}
                          className={`px-3 py-1.5 text-sm font-medium border rounded-lg transition-colors ${
                          formCustomerStatus.includes(option.value)
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                              : 'border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-secondary text-gray-700 dark:text-dark-text-primary hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Customer Created Date Filter */}
                <div>
                  <label className="block text-sm text-gray-600 dark:text-dark-text-muted mb-2">
                    Customer Created Date <span className="text-xs">(optional)</span>
                  </label>
                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                      {[
                        { value: '', label: 'Any Time' },
                        { value: 'after', label: 'After' },
                        { value: 'before', label: 'Before' },
                        { value: 'between', label: 'Between' },
                      ].map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => {
                            setFormDateFilterType(option.value as DateFilterType | '');
                            // Clear dates when changing filter type
                            if (option.value === '') {
                              setFormDateAfter('');
                              setFormDateBefore('');
                            }
                          }}
                          className={`px-3 py-1.5 text-sm font-medium border rounded-lg transition-colors ${
                            formDateFilterType === option.value
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                              : 'border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-secondary text-gray-700 dark:text-dark-text-primary hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    
                    {/* Date Inputs */}
                    {formDateFilterType && (
                      <div className="flex flex-wrap items-center gap-2">
                        {(formDateFilterType === 'after' || formDateFilterType === 'between') && (
                          <div className="flex items-center gap-2">
                            {formDateFilterType === 'between' && (
                              <span className="text-sm text-gray-500 dark:text-dark-text-muted">From</span>
                            )}
                            <input
                              type="date"
                              value={formDateAfter}
                              onChange={(e) => setFormDateAfter(e.target.value)}
                              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-dark-border rounded-lg dark:bg-dark-bg-secondary dark:text-dark-text-primary focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        )}
                        {(formDateFilterType === 'before' || formDateFilterType === 'between') && (
                          <div className="flex items-center gap-2">
                            {formDateFilterType === 'between' && (
                              <span className="text-sm text-gray-500 dark:text-dark-text-muted">To</span>
                            )}
                            <input
                              type="date"
                              value={formDateBefore}
                              onChange={(e) => setFormDateBefore(e.target.value)}
                              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-dark-border rounded-lg dark:bg-dark-bg-secondary dark:text-dark-text-primary focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions */}
                <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-text-primary bg-white dark:bg-dark-bg-secondary border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formSubmitting || !formName}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                    {formSubmitting ? 'Creating...' : 'Create Audience'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setListToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title={`Delete "${listToDelete?.name}"?`}
        message="This list will be permanently deleted. This action cannot be undone."
        isDeleting={isDeleting}
      />
    </div>
  );
  }
);

ListsManager.displayName = 'ListsManager';

export default ListsManager;
